/**
 * Emoji Domain
 * Manages team emoji communication state and operations
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OpCode, MessageType } from '../constants/op-codes';

// Import Lottie emoji animations - numbered (keys 1-9, 0)
import thumbsUpAnim from '../assets/emojis/thumbs-up.json';
import thumbsDownAnim from '../assets/emojis/thumbs-down.json';
import fireAnim from '../assets/emojis/fire.json';
import heartAnim from '../assets/emojis/heart.json';
import moneyWingsAnim from '../assets/emojis/money-wings.json';
import lightbulbAnim from '../assets/emojis/lightbulb.json';
import partyAnim from '../assets/emojis/party.json';
import eyesAnim from '../assets/emojis/eyes.json';
import skullAnim from '../assets/emojis/skull.json';
import helpAnim from '../assets/emojis/help.json';

// Import Lottie emoji animations - the faces, picked from the reactions only
import joyAnim from '../assets/emojis/laughing.json';
import thinkingAnim from '../assets/emojis/thinking.json';
import mindblownAnim from '../assets/emojis/mindblown.json';
import happyTearsAnim from '../assets/emojis/happy-tears.json';
import zanyAnim from '../assets/emojis/zany.json';
import { sendToMatch } from '../services/nakama/match-send';

// ==================== TYPES ====================

/**
 * Emoji definition with Lottie animation data.
 * IDs 0-9 have a key each; IDs 11-15 are picked from the reactions only.
 */
export interface EmojiDefinition {
  id: number;
  animationData: object;
  /** The key that names it in the player's language, written whole so every locale rule reads it. */
  nameKey: string;
  glyph: string;
}

/**
 * Numbered emojis with keyboard shortcuts (keys 1-9, 0)
 */
export const EMOJI_LIST: EmojiDefinition[] = [
  { id: 1, animationData: thumbsUpAnim, nameKey: 'emoji.thumbs_up', glyph: '👍' },
  { id: 2, animationData: thumbsDownAnim, nameKey: 'emoji.thumbs_down', glyph: '👎' },
  { id: 3, animationData: fireAnim, nameKey: 'emoji.fire', glyph: '🔥' },
  { id: 4, animationData: heartAnim, nameKey: 'emoji.heart', glyph: '❤️' },
  { id: 5, animationData: moneyWingsAnim, nameKey: 'emoji.money_wings', glyph: '💸' },
  { id: 6, animationData: lightbulbAnim, nameKey: 'emoji.lightbulb', glyph: '💡' },
  { id: 7, animationData: partyAnim, nameKey: 'emoji.party', glyph: '🎉' },
  { id: 8, animationData: eyesAnim, nameKey: 'emoji.eyes', glyph: '👀' },
  { id: 9, animationData: skullAnim, nameKey: 'emoji.skull', glyph: '💀' },
  { id: 0, animationData: helpAnim, nameKey: 'emoji.help', glyph: '🚨' },
];

/**
 * Facial reactions, picked from the reactions only (no key, IDs 11-15)
 */
export const EMOJI_FACES: EmojiDefinition[] = [
  { id: 11, animationData: joyAnim, nameKey: 'emoji.joy', glyph: '😂' },
  { id: 12, animationData: thinkingAnim, nameKey: 'emoji.thinking', glyph: '🤔' },
  { id: 13, animationData: mindblownAnim, nameKey: 'emoji.mindblown', glyph: '🤯' },
  { id: 14, animationData: happyTearsAnim, nameKey: 'emoji.happy_tears', glyph: '🥹' },
  { id: 15, animationData: zanyAnim, nameKey: 'emoji.zany', glyph: '🤪' },
];

/**
 * Every emoji the game knows
 */
const ALL_EMOJIS: EmojiDefinition[] = [...EMOJI_LIST, ...EMOJI_FACES];

/**
 * Active emoji displayed for a player
 */
interface ActiveEmoji {
  userId: string;
  emojiId: number;
  timestamp: number;
  expiresAt: number;
}

/**
 * Emoji settings
 */
interface EmojiSettings {
  displayDuration: number; // seconds
}

// ==================== STORE ====================

interface EmojiState {
  activeEmojis: Map<string, ActiveEmoji>; // Map of userId -> ActiveEmoji
  settings: EmojiSettings;
  _version: number; // Internal version counter to force re-renders
  addActiveEmoji: (userId: string, emojiId: number) => void;
  removeEmoji: (userId: string) => void;
  reset: () => void;
}

const DEFAULT_DISPLAY_DURATION = 2; // seconds

const initialState = {
  activeEmojis: new Map<string, ActiveEmoji>(),
  settings: {
    displayDuration: DEFAULT_DISPLAY_DURATION,
  },
  _version: 0,
};

/**
 * Emoji Store
 * Manages active emoji state for all players
 */
export const useEmojiStore = create<EmojiState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Add an active emoji for a user.
       * If the same emoji is already playing for this user, skip the update.
       */
      addActiveEmoji: (userId: string, emojiId: number) => {
        // Check if the same emoji is already active for this user
        const existingEmoji = get().activeEmojis.get(userId);
        if (existingEmoji && existingEmoji.emojiId === emojiId) {
          console.log('[EmojiStore] Same emoji already active, skipping:', { userId, emojiId });
          return;
        }

        const now = Date.now();
        const duration = get().settings.displayDuration * 1000; // convert to ms
        const expiresAt = now + duration;

        const emoji: ActiveEmoji = {
          userId,
          emojiId,
          timestamp: now,
          expiresAt,
        };

        console.log('[EmojiStore] Adding emoji:', { userId, emojiId, expiresAt });

        set((state) => {
          const newMap = new Map(state.activeEmojis);
          newMap.set(userId, emoji);
          return {
            activeEmojis: newMap,
            _version: state._version + 1, // Increment version to force re-render
          };
        }, false, 'addActiveEmoji');

        // Schedule removal after duration
        setTimeout(() => {
          const currentEmoji = get().activeEmojis.get(userId);
          if (currentEmoji && currentEmoji.timestamp === emoji.timestamp) {
            get().removeEmoji(userId);
          }
        }, duration);
      },

      /**
       * Remove an emoji for a user
       */
      removeEmoji: (userId: string) => {
        console.log('[EmojiStore] Removing emoji for user:', userId);
        set((state) => {
          const newMap = new Map(state.activeEmojis);
          newMap.delete(userId);
          return { 
            activeEmojis: newMap,
            _version: state._version + 1, // Increment version to force re-render
          };
        }, false, 'removeEmoji');
      },

      /**
       * Reset the emoji store to initial state
       */
      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'emoji-store',
    }
  )
);

// ==================== HANDLERS ====================

/**
 * Handle OpCodeEmojiReceived message
 * Displays emoji for the sending user
 */
export function handleEmojiReceived(data: { user_id: string; emoji_id: number }) {
  console.log('[EmojiHandler] Emoji received:', data);

  const { user_id, emoji_id } = data;

  // Validate emoji_id - check if it exists in any emoji list
  const emojiExists = ALL_EMOJIS.some((emoji) => emoji.id === emoji_id);
  if (!emojiExists) {
    console.error('[EmojiHandler] Invalid emoji_id (not found in ALL_EMOJIS):', emoji_id);
    return;
  }

  useEmojiStore.getState().addActiveEmoji(user_id, emoji_id);
}

// ==================== SERVICE METHODS ====================

/**
 * Send an emoji to team members
 * @param emojiId - ID of the emoji to send (0-9 from a key, 11-15 from the reactions)
 */
export async function sendEmoji(emojiId: number): Promise<void> {
  // Validate emoji_id
  const emojiExists = ALL_EMOJIS.some((emoji) => emoji.id === emojiId);
  if (!emojiExists) {
    console.error('[EmojiService] Invalid emoji_id (not found in ALL_EMOJIS):', emojiId);
    return;
  }

  const message = {
    type: MessageType.MessageTypeSendEmoji,
    emoji_id: emojiId,
  };

  console.log('[EmojiService] Sending emoji:', emojiId, 'Message:', message);

  await sendToMatch(OpCode.OpCodeSendEmoji, message, 'an emoji');
}

/**
 * Get emoji definition by ID
 */
export function getEmojiById(emojiId: number): EmojiDefinition | undefined {
  return ALL_EMOJIS.find((emoji) => emoji.id === emojiId);
}
